// 1. Create a fetch util using fetch, which have 4 functions: get, post, put, delete
// 2. Each function should return a promise
// 3. Each function should have 2 parameters: url and data
// 4. If the method is get, the data should be appended to the url
// 5. If the method is post, put, delete, the data should be passed as a body
// 6. Use typescript with fully typed
// 7. Use async await
import qs from "qs"

import { filterEmptyValues } from "@/utils/query-string-api-helper"

const PREFIX_URL = process.env.NEXT_PUBLIC_API_URL

export type RequestMethod = "GET" | "POST" | "PUT" | "DELETE"
export type RequestData = Record<string, unknown> | null | undefined

export interface FetchOptions {
  url: string
  data?: RequestData
  otherOptions?: RequestInit
}

const normalizeUrl = (url: string): string => {
  const normalizedPath = url.startsWith("/") ? url : `/${url}`
  const apiPath = normalizedPath.startsWith("/api/") ? normalizedPath : `/api${normalizedPath}`

  return `${PREFIX_URL}${apiPath}`
}

const httpClient = {
  async request<T>(method: RequestMethod, { url, data, otherOptions }: FetchOptions): Promise<T> {
    const timeoutMs = 10000
    const timeoutSignal =
      typeof AbortSignal !== "undefined" && typeof (AbortSignal as any).timeout === "function"
        ? (AbortSignal as any).timeout(timeoutMs)
        : undefined

    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      // Timeout to prevent hanging requests (safe across browsers/runtimes)
      signal: timeoutSignal,
      ...otherOptions,
    }

    if (method === "GET" && data != null) {
      const filtered = filterEmptyValues(data)
      const queryString = qs.stringify(filtered, { encodeValuesOnly: true })
      if (queryString) {
        url += url.includes("?") ? `&${queryString}` : `?${queryString}`
      }
    } else if (data != null) {
      options.body = JSON.stringify(data)
    }

    const response = await fetch(normalizeUrl(url), options)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw {
        status: response.status,
        statusText: response.statusText,
        message: errorData.message || errorData.error || "Có lỗi xảy ra",
        data: errorData,
      }
    }

    return response.json() as Promise<T>
  },

  get<T>(url: string, data?: RequestData, options?: RequestInit): Promise<T> {
    return this.request("GET", { url, data, otherOptions: options })
  },

  post<T>(url: string, data: any): Promise<T> {
    return this.request("POST", { url, data })
  },

  put<T>(url: string, data: any): Promise<T> {
    return this.request("PUT", { url, data })
  },

  delete<T>(url: string, data: any): Promise<T> {
    return this.request("DELETE", { url, data })
  },
}

export default httpClient
